import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Check, Loader2, Percent, Layers, RotateCcw, Search, Building2 } from 'lucide-react';
import { cn } from '@lib/utils';
import {
  commissionService,
  type CommissionConfig,
  type CategoryRate,
  type BusinessCommission,
  type DeliveryTaxConfig,
} from '@services/commission.service';
import { businessesService } from '@services/businesses.service';
import type { Business } from '@apptypes/index';

export const CommissionConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [savingFlat, setSavingFlat] = useState(false);
  const [flatRate, setFlatRate] = useState('10');
  const [rates, setRates] = useState<CategoryRate[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const loadDefaults = async () => {
    setLoading(true);
    try {
      const [cfg, cats] = await Promise.all([
        commissionService.getConfig() as Promise<CommissionConfig>,
        commissionService.listCategoryRates() as Promise<CategoryRate[]>,
      ]);
      setFlatRate(String(cfg.flatRate ?? 10));
      setRates(cats);
      setDraft(Object.fromEntries(cats.map((c) => [c.categoryId, c.rate != null ? String(c.rate) : ''])));
    } catch {
      toast.error('Could not load commission defaults');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void loadDefaults(); }, []);

  const saveFlat = async () => {
    const r = parseFloat(flatRate);
    if (Number.isNaN(r) || r < 0 || r > 100) { toast.error('Flat rate must be 0–100'); return; }
    setSavingFlat(true);
    try {
      await commissionService.setConfig({ flatRate: r });
      toast.success('Default flat rate saved');
    } catch { toast.error('Failed to save'); } finally { setSavingFlat(false); }
  };

  const saveDefaultCat = async (categoryId: string) => {
    const r = parseFloat(draft[categoryId]);
    if (Number.isNaN(r) || r < 0 || r > 100) { toast.error('Rate must be 0–100'); return; }
    try { await commissionService.setCategoryRate(categoryId, r); toast.success('Default category rate set'); await loadDefaults(); }
    catch { toast.error('Failed to set rate'); }
  };
  const clearDefaultCat = async (categoryId: string) => {
    try { await commissionService.clearCategoryRate(categoryId); toast.success('Category default cleared'); await loadDefaults(); }
    catch { toast.error('Failed to clear'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32 text-[var(--text-muted)]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="p-8 space-y-6 max-w-[1000px]">
      <div>
        <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">Commission Configuration</h1>
        <p className="text-[14px] text-[var(--text-muted)] mt-1">
          Set the platform defaults, then optionally override how commission is computed per business.
          Commission is always charged on the VAT‑exclusive base. Merchants can only view their split.
        </p>
      </div>

      <div className="rounded-3xl bg-[var(--state-warning-bg)] border border-[var(--state-warning-border)] p-5 flex items-start gap-4">
        <div className="rounded-2xl p-2.5 bg-white text-[var(--state-warning)] flex-shrink-0"><AlertTriangle className="w-5 h-5" strokeWidth={1.5} /></div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Resolution per sale: <span className="font-semibold">business override → platform category default → platform flat rate</span>.
          Rate changes apply to sales confirmed afterwards.
        </div>
      </div>

      {/* Platform defaults */}
      <div className="rounded-3xl bg-white shadow-soft p-6 space-y-5">
        <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Platform defaults</h2>
        <div className="flex items-end gap-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">Default flat rate</label>
            <div className="relative w-40">
              <input type="number" step="0.1" min="0" max="100" value={flatRate} onChange={(e) => setFlatRate(e.target.value)}
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 pr-9 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)]" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[14px]">%</span>
            </div>
          </div>
          <button onClick={saveFlat} disabled={savingFlat}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-6 py-3 text-[14px] font-semibold text-white hover:bg-[var(--brand-forest)] transition-all disabled:opacity-50">
            {savingFlat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={1.5} />} Save
          </button>
          <p className="text-[12px] text-[var(--text-muted)] pb-3">Used when a business has no config, and as the fallback for categories without a rate.</p>
        </div>

        <div>
          <p className="text-[13px] font-semibold text-[var(--text-primary)] mb-2">Default category rates</p>
          <div className="rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">Category</th>
                <th className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">Rate</th>
                <th className="px-5 py-2.5 text-right" />
              </tr></thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.categoryId} className="border-b border-[var(--border-subtle)] last:border-0">
                    <td className="px-5 py-2.5 text-[14px] text-[var(--text-primary)]">{r.categoryName}</td>
                    <td className="px-5 py-2.5">
                      <div className="relative w-24">
                        <input type="number" step="0.1" min="0" max="100" placeholder={`${flatRate} flat`}
                          value={draft[r.categoryId] ?? ''} onChange={(e) => setDraft((d) => ({ ...d, [r.categoryId]: e.target.value }))}
                          className="w-full rounded-xl border border-[var(--border-default)] bg-white px-3 py-1.5 pr-6 text-[13px] focus:border-[var(--border-strong)]" />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[12px]">%</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => saveDefaultCat(r.categoryId)} disabled={!draft[r.categoryId]}
                        className="rounded-full bg-[var(--brand-forest)] px-3.5 py-1.5 text-[12px] font-semibold text-white hover:bg-[var(--brand-forest)] disabled:opacity-40 mr-2">Set</button>
                      {r.rate != null && (
                        <button onClick={() => clearDefaultCat(r.categoryId)}
                          className="rounded-full border border-[var(--border-default)] px-3.5 py-1.5 text-[12px] font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)]">Clear</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Per-business */}
      <BusinessCommissionSection />

      {/* Driver payout tax model */}
      <DeliveryTaxSection />
    </div>
  );
};

// ── Driver payout & tax model ────────────────────────────────────────────────

const TAX_MODELS = [
  {
    value: 'trip_vatable',
    title: 'Trip is VATable',
    desc: 'The trip fee is VAT‑inclusive (116%). VAT is removed first, commission is taken from the base, the driver gets the rest. VAT goes to the tax float.',
  },
  {
    value: 'commission_vatable',
    title: 'VAT on commission',
    desc: "The trip isn't VATable, but Chekit's commission is. VAT is charged on the commission, and who bears it is configurable below.",
  },
  {
    value: 'no_vat',
    title: 'No VAT',
    desc: 'Plain deduction: commission off the trip fee, the driver gets the rest.',
  },
] as const;

const DeliveryTaxSection = () => {
  const [cfg, setCfg] = useState<DeliveryTaxConfig | null>(null);
  const [rate, setRate] = useState('20');
  const [vatRate, setVatRate] = useState('16');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const c = await commissionService.getDeliveryConfig() as DeliveryTaxConfig;
      setCfg(c);
      setRate(String(c.commissionRate));
      setVatRate(String(c.vatRate));
    } catch { toast.error('Could not load driver payout config'); }
  };
  useEffect(() => { void load(); }, []);

  const save = async (patch: Partial<DeliveryTaxConfig>) => {
    setSaving(true);
    try {
      await commissionService.setDeliveryConfig(patch);
      toast.success('Driver payout config saved');
      await load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  if (!cfg) return null;

  // Worked example for the selected model (fee = 100)
  const fee = 100;
  const r = parseFloat(rate) || 0;
  const v = parseFloat(vatRate) || 0;
  let example = '';
  if (cfg.taxModel === 'trip_vatable') {
    const base = fee * 100 / (100 + v);
    const comm = base * r / 100;
    example = `Fee 100 → base ${base.toFixed(2)}, VAT ${(fee - base).toFixed(2)} → tax float, commission ${comm.toFixed(2)}, driver gets ${(base - comm).toFixed(2)}`;
  } else if (cfg.taxModel === 'commission_vatable') {
    const comm = fee * r / 100;
    const vat = comm * v / 100;
    example = cfg.commissionVatPayer === 'driver'
      ? `Fee 100 → commission ${comm.toFixed(2)}, VAT ${vat.toFixed(2)} (driver pays) → driver gets ${(fee - comm - vat).toFixed(2)}`
      : `Fee 100 → commission ${(comm - vat).toFixed(2)} (Chekit absorbs VAT ${vat.toFixed(2)}) → driver gets ${(fee - comm).toFixed(2)}`;
  } else {
    const comm = fee * r / 100;
    example = `Fee 100 → commission ${comm.toFixed(2)}, driver gets ${(fee - comm).toFixed(2)}`;
  }

  return (
    <div className="rounded-3xl bg-white shadow-soft p-6 space-y-5">
      <div>
        <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Driver payout &amp; tax</h2>
        <p className="text-[13px] text-[var(--text-muted)] mt-0.5">
          How the trip fee is taxed and split between the driver and Chekit. Every release stores
          a full snapshot in the journal, so historical orders stay auditable when this changes.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {TAX_MODELS.map((m) => {
          const active = cfg.taxModel === m.value;
          return (
            <button key={m.value} disabled={saving} onClick={() => save({ taxModel: m.value })}
              className={cn('text-left rounded-2xl border p-4 transition-all', active ? 'border-[var(--border-strong)] bg-[var(--bg-base)]' : 'border-[var(--border-subtle)] hover:border-[var(--border-default)]')}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">{m.title}</span>
                {active && <Check className="w-4 h-4 text-[var(--text-accent)] ml-auto" strokeWidth={2.5} />}
              </div>
              <p className="text-[12px] text-[var(--text-muted)]">{m.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">Commission rate</label>
          <div className="relative w-32">
            <input type="number" step="0.1" min="0" max="100" value={rate} onChange={(e) => setRate(e.target.value)}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 pr-8 text-[14px] focus:border-[var(--border-strong)]" />
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[14px]">%</span>
          </div>
        </div>
        {cfg.taxModel !== 'no_vat' && (
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">VAT rate</label>
            <div className="relative w-32">
              <input type="number" step="0.1" min="0" max="100" value={vatRate} onChange={(e) => setVatRate(e.target.value)}
                className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 pr-8 text-[14px] focus:border-[var(--border-strong)]" />
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[14px]">%</span>
            </div>
          </div>
        )}
        {cfg.taxModel === 'commission_vatable' && (
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">VAT borne by</label>
            <div className="flex gap-2">
              {(['chekit', 'driver'] as const).map((p) => (
                <button key={p} disabled={saving} onClick={() => save({ commissionVatPayer: p })}
                  className={cn('rounded-full px-4 py-2.5 text-[13px] font-semibold transition-all',
                    cfg.commissionVatPayer === p ? 'bg-[var(--brand-forest)] text-white' : 'border border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]')}>
                  {p === 'chekit' ? 'Chekit (off commission)' : 'Driver (off payout)'}
                </button>
              ))}
            </div>
          </div>
        )}
        <button onClick={() => save({ commissionRate: parseFloat(rate), vatRate: parseFloat(vatRate) })} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-6 py-2.5 text-[14px] font-semibold text-white hover:bg-[var(--brand-forest)] transition-all disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={1.5} />}
          Save rates
        </button>
      </div>

      <div className="rounded-2xl bg-[var(--bg-base)] border border-[var(--border-subtle)] px-4 py-3 text-[13px] text-[var(--text-secondary)]">
        <span className="font-semibold text-[var(--text-primary)]">Example: </span>{example}
      </div>
    </div>
  );
};

// ── Per-business override panel ──────────────────────────────────────────────

const BusinessCommissionSection = () => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Business[]>([]);
  const [selected, setSelected] = useState<Business | null>(null);
  const [cfg, setCfg] = useState<BusinessCommission | null>(null);
  const [bizFlat, setBizFlat] = useState('');
  const [catDraft, setCatDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    try { setResults((await businessesService.list({ search: search.trim(), limit: 8 })) as Business[]); }
    catch { toast.error('Search failed'); }
  };

  const loadBiz = async (b: Business) => {
    setSelected(b); setResults([]); setSearch(b.name);
    try {
      const c = await commissionService.getBusinessCommission(b.id) as BusinessCommission;
      setCfg(c);
      setBizFlat(c.flatRate != null ? String(c.flatRate) : '');
      setCatDraft(Object.fromEntries(c.categoryRates.map((r) => [r.categoryId, r.businessRate != null ? String(r.businessRate) : ''])));
    } catch { toast.error('Could not load business commission'); }
  };

  const setMode = async (mode: 'default' | 'flat' | 'category') => {
    if (!selected || !cfg) return;
    setBusy(true);
    try {
      const flat = mode === 'flat' ? (parseFloat(bizFlat) || cfg.platformFlatRate) : undefined;
      await commissionService.setBusinessCommission(selected.id, { mode, flatRate: flat });
      toast.success('Business commission updated');
      await loadBiz(selected);
    } catch { toast.error('Failed to update'); } finally { setBusy(false); }
  };

  const saveBizFlat = async () => {
    if (!selected) return;
    const r = parseFloat(bizFlat);
    if (Number.isNaN(r) || r < 0 || r > 100) { toast.error('Rate must be 0–100'); return; }
    try { await commissionService.setBusinessCommission(selected.id, { mode: 'flat', flatRate: r }); toast.success('Saved'); await loadBiz(selected); }
    catch { toast.error('Failed'); }
  };

  const saveBizCat = async (categoryId: string) => {
    if (!selected) return;
    const r = parseFloat(catDraft[categoryId]);
    if (Number.isNaN(r) || r < 0 || r > 100) { toast.error('Rate must be 0–100'); return; }
    try { await commissionService.setBusinessCategoryRate(selected.id, categoryId, r); toast.success('Override set'); await loadBiz(selected); }
    catch { toast.error('Failed'); }
  };
  const clearBizCat = async (categoryId: string) => {
    if (!selected) return;
    try { await commissionService.clearBusinessCategoryRate(selected.id, categoryId); toast.success('Override cleared'); await loadBiz(selected); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="rounded-3xl bg-white shadow-soft p-6 space-y-5">
      <div>
        <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">Per‑business override</h2>
        <p className="text-[13px] text-[var(--text-muted)] mt-0.5">Find a business and choose how its commission is computed.</p>
      </div>

      <div className="relative max-w-md">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[var(--text-muted)] absolute left-3.5 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()}
              placeholder="Search business by name…"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-2.5 text-[14px] focus:border-[var(--border-strong)]" />
          </div>
          <button onClick={doSearch} className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-[var(--brand-forest)]">Search</button>
        </div>
        {results.length > 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-2xl border border-[var(--border-subtle)] bg-white shadow-lift overflow-hidden">
            {results.map((b) => (
              <button key={b.id} onClick={() => loadBiz(b)}
                className="w-full text-left px-4 py-2.5 text-[14px] hover:bg-[var(--bg-base)] flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} /> {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && cfg && (
        <div className="space-y-5 border-t border-[var(--border-subtle)] pt-5">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[var(--text-primary)]" strokeWidth={1.5} />
            <span className="text-[15px] font-semibold text-[var(--text-primary)]">{selected.name}</span>
          </div>

          {/* Mode */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {([
              { value: 'default', icon: RotateCcw, title: 'Use default', desc: `Platform flat ${cfg.platformFlatRate}% on everything.` },
              { value: 'flat', icon: Percent, title: 'Flat', desc: 'A custom flat rate for this business.' },
              { value: 'category', icon: Layers, title: 'Per category', desc: 'Category defaults, with optional overrides.' },
            ] as const).map((opt) => {
              const active = cfg.mode === opt.value; const Icon = opt.icon;
              return (
                <button key={opt.value} disabled={busy} onClick={() => setMode(opt.value)}
                  className={cn('text-left rounded-2xl border p-4 transition-all', active ? 'border-[var(--border-strong)] bg-[var(--bg-base)]' : 'border-[var(--border-subtle)] hover:border-[var(--border-default)]')}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={cn('w-4 h-4', active ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]')} strokeWidth={1.5} />
                    <span className="text-[14px] font-semibold text-[var(--text-primary)]">{opt.title}</span>
                    {active && <Check className="w-4 h-4 text-[var(--text-accent)] ml-auto" strokeWidth={2.5} />}
                  </div>
                  <p className="text-[12px] text-[var(--text-muted)]">{opt.desc}</p>
                </button>
              );
            })}
          </div>

          {cfg.mode === 'flat' && (
            <div className="flex items-end gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">Business flat rate</label>
                <div className="relative w-36">
                  <input type="number" step="0.1" min="0" max="100" value={bizFlat} onChange={(e) => setBizFlat(e.target.value)}
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 pr-8 text-[14px] focus:border-[var(--border-strong)]" />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[14px]">%</span>
                </div>
              </div>
              <button onClick={saveBizFlat} className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-[var(--brand-forest)]">Save rate</button>
            </div>
          )}

          {cfg.mode === 'category' && (
            <div className="rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
                  <th className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">Category</th>
                  <th className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">Default</th>
                  <th className="px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] text-left">Override</th>
                  <th />
                </tr></thead>
                <tbody>
                  {cfg.categoryRates.map((r) => (
                    <tr key={r.categoryId} className="border-b border-[var(--border-subtle)] last:border-0">
                      <td className="px-5 py-2.5 text-[14px] text-[var(--text-primary)]">{r.categoryName}</td>
                      <td className="px-5 py-2.5 text-[13px] text-[var(--text-muted)]">
                        {r.platformRate != null ? `${r.platformRate}%` : `${cfg.platformFlatRate}% (flat)`}
                      </td>
                      <td className="px-5 py-2.5">
                        <div className="relative w-24">
                          <input type="number" step="0.1" min="0" max="100" placeholder="inherit"
                            value={catDraft[r.categoryId] ?? ''} onChange={(e) => setCatDraft((d) => ({ ...d, [r.categoryId]: e.target.value }))}
                            className="w-full rounded-xl border border-[var(--border-default)] bg-white px-3 py-1.5 pr-6 text-[13px] focus:border-[var(--border-strong)]" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] text-[12px]">%</span>
                        </div>
                      </td>
                      <td className="px-5 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => saveBizCat(r.categoryId)} disabled={!catDraft[r.categoryId]}
                          className="rounded-full bg-[var(--brand-forest)] px-3.5 py-1.5 text-[12px] font-semibold text-white hover:bg-[var(--brand-forest)] disabled:opacity-40 mr-2">Set</button>
                        {r.businessRate != null && (
                          <button onClick={() => clearBizCat(r.categoryId)}
                            className="rounded-full border border-[var(--border-default)] px-3.5 py-1.5 text-[12px] font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)]">Inherit</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
